import { Interactive, useController } from "@react-three/xr";
import React from "react";
import * as THREE from "three";
import { Asset, Hotspot } from "../../../../../Types";
import {
  UseAssetsQuery,
  UseDownloadAsset,
} from "../../../../../api/calls/Queries";
import { Center, Html, Svg } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import { useVolumeStore } from "../../../../../state management/zustand/common-stores";
import { useCursorShrinkingStore } from "../../UI/CursorNew";
import { useXRPopupStore } from "../XRHotspots";
import { useDataRootStore } from "../../../../../api/Zustand";
import XRHotspotLabel from "./XRHotspotLabel";

let timer: NodeJS.Timeout;
let timer2: NodeJS.Timeout;

const XRAudio = (props: {
  pos: THREE.Vector3 | [number, number, number];
  hs: Hotspot;
  tourID: number;
}) => {
  const { data: audioList, isLoading } = UseAssetsQuery(props.tourID, 3);

  const gaze = useController("none");

  const currentAudio: Asset | undefined = React.useMemo(() => {
    if (audioList)
      return audioList.find((one) => one.assetId === props.hs.target);
  }, [audioList, props.hs]);

  const { data: audioBlob, isLoading: isDownloadingAudio } = UseDownloadAsset(
    currentAudio?.assetKeyFull
  );

  const ref = React.useRef<HTMLAudioElement>(null);

  const audio = React.useRef(new Audio());

  const { volume } = useVolumeStore();

  const [playing, setPlaying] = React.useState<boolean>(false);

  const iconRef = React.useRef<THREE.Mesh>(null);
  const labelPosRef = React.useRef<THREE.Group>(null);
  const labelRef = React.useRef(new THREE.Mesh());
  const labelTextRef = React.useRef(new THREE.Object3D());

  const [hover, setHover] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (audioBlob) {
      audio.current.src = audioBlob;
    }
  }, [audioBlob]);

  React.useEffect(() => {
    if (ref.current) {
      ref.current.volume = volume;
    }
  }, [volume]);

  const cursor = useCursorShrinkingStore();

  const { root } = useDataRootStore();
  useFrame(() => {
    if (hover === props.hs.id) {
      iconRef.current?.scale.lerp(new THREE.Vector3(1, 1, 1), 0.1);
      if (labelPosRef.current) {
        labelPosRef.current.scale.lerp(new THREE.Vector3(1, 1, 1), 0.1);
      }
    } else {
      iconRef.current?.scale.lerp(new THREE.Vector3(0.8, 0.8, 0.8), 0.1);
      if (labelPosRef.current) {
        labelPosRef.current.scale.lerp(new THREE.Vector3(0, 0, 0), 0.1);
      }
    }

    if (labelTextRef.current && labelRef.current) {
      const finite: number =
        //@ts-expect-error
        labelTextRef.current.geometry?.boundingBox.max.length();

      if (isFinite(finite) && !labelRef.current.geometry.index) {
        //@ts-expect-error
        const box: THREE.Box3 = labelTextRef.current.geometry?.boundingBox;

        const size3 = new THREE.Vector3();

        box.getSize(size3);
        size3.multiplyScalar(1.2);

        labelRef.current.geometry = new THREE.PlaneGeometry(size3.x, size3.y);

        return;
      } else return;
    }
  });

  const xrPop = useXRPopupStore();

  return (
    <group
      visible={!xrPop.isOpen}
      scale={gaze ? 1.6 : 0.6}
      position={props.pos}
      onUpdate={(self) => self.lookAt(0, 0, 0)}
    >
      <Interactive
        onHover={() => {
          if (xrPop.isOpen) return;
          setHover(props.hs.id);
          clearTimeout(timer);
          clearTimeout(timer2);
          cursor.setShrinking(false);
          if (ref.current && gaze) {
            timer = setTimeout(() => {
              cursor.setShrinking(true);
              timer2 = setTimeout(() => {
                if (ref.current) {
                  if (ref.current.paused) ref.current.play();
                  else ref.current.pause();
                }
                cursor.setShrinking(false);
              }, 2e3);
            }, 1e3);
          }
        }}
        onBlur={() => {
          setHover(null);
          clearTimeout(timer);
          clearTimeout(timer2);
          cursor.setShrinking(false);
        }}
        onSelect={() => {
          if (xrPop.isOpen) return;
          if (ref.current) {
            cursor.setShrinking(false);

            if (ref.current.paused) ref.current.play();
            else ref.current.pause();
          }
        }}
      >
        <Center>
          <mesh ref={iconRef}>
            <Svg
              visible={!playing}
              src={root + "/svg/headphones.svg"}
              scale={0.0325}
              position={[-15.5, -16, 0]}
              fillMaterial={{
                color: "#fff",
                depthTest: false,
                transparent: true,
              }}
              strokeMaterial={{ color: "#000", depthTest: false }}
              strokeMeshProps={{ position: [0, 0, 1] }}
            />

            <Svg
              visible={playing}
              src={root + "/svg/pause.svg"}
              fillMaterial={{
                color: "#fff",
                depthTest: false,
                transparent: true,
              }}
              strokeMaterial={{ color: "#000", depthTest: false }}
              strokeMeshProps={{ position: [0, 0, 1] }}
              scale={0.0325}
              position={[-15.5, -16, 0]}
            />
          </mesh>
          <mesh renderOrder={2} name="hotspot" visible={false}>
            <planeGeometry args={[20, 20]} />
            <meshPhongMaterial color={playing ? "white" : "red"} wireframe />
          </mesh>
        </Center>

        <XRHotspotLabel hoverState={hover === props.hs.id}>
          {props.hs.title ?? ""}
        </XRHotspotLabel>

        {/* <group position={[0, -20, 0]} ref={labelPosRef} scale={0}>
            <Text
              fontSize={6}
              position={[0, 0, 5]}
              maxWidth={50}
              overflowWrap="break-word"
              textAlign="center"
              ref={labelTextRef}
            >
              {props.hs.title}
            </Text>

            <mesh ref={labelRef} material={labelMaterial}></mesh>
          </group>
            */}
      </Interactive>

      {audioBlob && (
        <Html>
          <audio
            src={audioBlob}
            ref={ref}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
          ></audio>
        </Html>
      )}
    </group>
  );
};

export default XRAudio;
